<template>
  <div class="">
    <h2
      v-if="label.length > 0"
      class="text-md mb-2">
      {{ label }}
    </h2>
    <Combobox
      v-model="selected"
      multiple>
      <div class="relative mt-1">
        <div
          class="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none ring-1 ring-black ring-opacity-20 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-300 sm:text-sm">
          <ComboboxInput
            class="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-600 focus:ring-0"
            :display-value="() => ''"
            :placeholder="placeholderName"
            autocomplete="off"
            @change="query = $event.target.value" />
          <ComboboxButton
            class="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-600"
              aria-hidden="true" />
          </ComboboxButton>
        </div>
        <TransitionRoot
          leave="transition ease-in duration-100"
          leave-from="opacity-100"
          leave-to="opacity-0">
          <ComboboxOptions
            class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-20 focus:outline-none sm:text-sm z-10">
            <div
              v-if="query === ''"
              class="relative cursor-default select-none py-2 px-4 text-gray-600">
              Start typing to search.
            </div>
            <div
              v-else-if="loading"
              class="relative cursor-disabled select-none">
              <svg
                role="status"
                class="w-4 h-4 m-2 text-gray-200 animate-spin dark:text-gray fill-blue"
                viewBox="0 0 100 101"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor" />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill" />
              </svg>
            </div>
            <div
              v-else-if="filteredResult.length === 0"
              class="relative cursor-default select-none py-2 px-4 text-font-light">
              Nothing found.
            </div>
            <template v-else>
              <ComboboxOption
                v-for="item in selected"
                v-if="selected.length > 0"
                :value="item">
                <li
                  class="text-font flex flex-row relative cursor-default select-none py-2 pl-10 pr-4">
                  <slot
                    :item="item"
                    :selected="true">
                    <span
                      class="block truncate"
                      :class="{ 'font-medium': selected, 'font-normal': !selected }">
                      <img
                        :src="item.image"
                        alt=""
                        class="flex-shrink-0 rounded-sm h-4 w-6 mr-3">
                    </span>
                    <span
                      class="block truncate font-medium">
                      {{ item.name }}
                    </span>
                    <span
                      class="block truncate mr-3 "
                      :class="{ 'font-medium': selected, 'font-light': !selected }">
                      {{ item.area }}
                    </span>
                    <span
                      class="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                      <CheckIcon
                        class="h-5 w-5"
                        aria-hidden="true" />
                    </span>
                  </slot>
                </li>
              </ComboboxOption>
              <ComboboxOption
                v-for="item in filteredResult"
                :key="item.id"
                v-slot="{ selected, active }"
                as="template"
                :value="item">
                <li
                  class="relative flex flex-row cursor-default select-none py-2 pl-10 pr-4"
                  :class="{
                    'bg-primary text-white': active,
                    'text-gray-600': !active,
                  }">
                  <slot
                    :item="item"
                    :selected="selected"
                    :active="active">
                    <span
                      class="block truncate"
                      :class="{ 'font-medium': selected, 'font-normal': !selected }">
                      <img
                        :src="item.image"
                        alt=""
                        class="flex-shrink-0 rounded-sm h-4 w-6 mr-3">
                    </span>

                    <span
                      class="block truncate mr-3 font-medium "
                      :class="{ 'font-gray-900': selected, 'font-normal': !selected }">
                      {{ item.name }}
                    </span>

                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3"
                      :class="{ 'text-font': active, 'text-primary': !active }">
                      <CheckIcon
                        class="h-5 w-5"
                        aria-hidden="true" />
                    </span>
                  </slot>
                </li>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
      <div
        v-if="selected && selected.length > 0"
        class="mt-2 space-x-2">
        <span
          v-for="(item, index) in selected"
          :key="item.id"
          class="inline-flex items-center py-1 pl-2 pr-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
          <slot
            name="label"
            :item="item">
            {{ item.name }}
          </slot>
          <button
            type="button"
            class="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none focus:bg-indigo-500 focus:text-white"
            @click="remove(index)">
            <span class="sr-only">Remove {{ item.name }} from selection</span>
            <svg
              class="h-2 w-2"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 8 8">
              <path
                stroke-linecap="round"
                stroke-width="1.5"
                d="M1 1l6 6m0-6L1 7" />
            </svg>
          </button>
        </span>
      </div>
    </Combobox>
  </div>
</template>

<script setup>
import {
  ref, computed, watch,
} from 'vue';
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
  ComboboxButton,
} from '@headlessui/vue';
import {
  CheckIcon, ChevronUpDownIcon,
} from '@heroicons/vue/24/solid';

const props = defineProps({
  modelValue: {
    type: Array,
    required: false,
    default: [],
  },
  preExistingValue: {
    type: Array,
    required: false,
    default: [],
  },
  apiValue: {
    type: String,
    required: true,
    default: '',
  },
  placeholderValue: {
    type: String,
    required: false,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
});
const placeholderName = ref(props.placeholderValue);
const selected = ref(props.modelValue);
const preExistingValue = computed(() => props.preExistingValue);
const query = ref('');
const loading = ref(false);
const result = ref([]);
let timer;

const filteredResult = computed(() => {
  if (preExistingValue.value.length > 0) {
    return result.value
      .filter((item) => !selected.value.map((l) => l.id).includes(item.id))
      .filter((item) => !preExistingValue.value.map((l) => l.id).includes(item.id));
  }

  return result.value.filter((item) => !selected.value.map((l) => l.id).includes(item.id));
});

function remove(index) {
  selected.value.splice(index, 1);
}

function search() {
  loading.value = true;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  timer = setTimeout(() => {
    axios
      .get(props.apiValue + query.value)
      .then((response) => {
        if (typeof response.data === 'object') {
          result.value = Object.values(response.data);
        } else {
          result.value = response.data;
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        loading.value = false;
      });
  }, 500);
}

watch(query, () => {
  search();
}, { deep: true });

const emit = defineEmits({ update: (_value) => true });

watch(selected, () => {
  emit('update:modelValue', selected.value);
});

</script>
